import React, { useState, useRef } from 'react';
import { useAuth, createApiClient } from '../App';
import { toast } from 'sonner';
import {
  Camera,
  SpinnerGap,
  Check,
  X,
  Star,
  MapPin,
  PencilSimple,
  ArrowRight,
  Image as ImageIcon,
  ForkKnife,
  CalendarBlank,
  Users,
  Receipt
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';

const PhotoVisitModal = ({ open, onOpenChange, onVisitCreated }) => {
  const { token } = useAuth();
  const api = createApiClient(token);
  const fileInputRef = useRef(null);

  const [step, setStep] = useState('upload'); // upload | analyzing | review | saving
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [result, setResult] = useState(null);

  // Editable fields
  const [restaurantName, setRestaurantName] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [billTotal, setBillTotal] = useState('');
  const [tip, setTip] = useState('');
  const [partySize, setPartySize] = useState('');
  const [mealType, setMealType] = useState('');
  const [rating, setRating] = useState(0);

  const resetState = () => {
    setStep('upload');
    setImageFile(null);
    setImagePreview(null);
    setResult(null);
    setRestaurantName('');
    setVisitDate('');
    setBillTotal('');
    setTip('');
    setPartySize('');
    setMealType('');
    setRating(0);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleAnalyze = async () => {
    if (!imageFile) return;
    setStep('analyzing');

    const formData = new FormData();
    formData.append('file', imageFile);

    try {
      const res = await api.post('/visits/from-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(res.data);
      setRestaurantName(res.data.restaurant_name || '');
      setVisitDate(res.data.visit_date || new Date().toISOString().split('T')[0]);
      setBillTotal(res.data.bill_total || '');
      setTip(res.data.tip || '');
      setPartySize(res.data.party_size || '');
      setMealType(res.data.meal_type || '');
      setStep('review');
    } catch (error) {
      toast.error('Failed to analyze photo');
      setStep('upload');
    }
  };

  const handleConfirm = async () => {
    if (!restaurantName.trim()) {
      toast.error('Restaurant name is required');
      return;
    }
    setStep('saving');

    try {
      const payload = {
        restaurant_id: result?.matched_restaurant?.id || null,
        restaurant_name: restaurantName,
        visit_date: visitDate || null,
        bill_total: billTotal ? parseFloat(billTotal) : null,
        tip: tip ? parseFloat(tip) : null,
        party_size: partySize ? parseInt(partySize) : null,
        meal_type: mealType || null,
        notable_dishes: result?.notable_dishes || [],
        ambience_notes: result?.ambience_notes || null,
        overall_rating: rating || null,
        items: result?.items || [],
        attachment: result?.attachment || null,
      };

      const visit = await api.post('/visits/confirm-from-photo', payload);
      toast.success(`Visit to ${restaurantName} logged!`);
      onVisitCreated?.(visit.data);
      resetState();
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to save visit');
      setStep('review');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetState(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto" data-testid="photo-visit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera weight="duotone" className="w-5 h-5 text-[#A94438]" />
            {step === 'upload' ? 'Snap & Log Visit' : step === 'analyzing' ? 'Analyzing...' : 'Review & Confirm'}
          </DialogTitle>
        </DialogHeader>

        {/* Upload Step */}
        {step === 'upload' && (
          <div className="space-y-4">
            <p className="text-sm text-[#7A7873]">
              Upload a photo of your receipt, the restaurant, or your food — AI will extract the details.
            </p>
            {imagePreview ? (
              <div className="relative">
                <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-xl" />
                <button
                  onClick={() => { setImageFile(null); setImagePreview(null); }}
                  className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-full"
                >
                  <X weight="bold" className="w-4 h-4 text-red-500" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-48 border-2 border-dashed border-[#E8DCD0] rounded-xl flex flex-col items-center justify-center hover:border-[#A94438]/40 hover:bg-[#F9F8F5] transition-colors"
                data-testid="photo-upload-area"
              >
                <Camera weight="duotone" className="w-10 h-10 text-[#B5B0AA] mb-2" />
                <p className="text-sm text-[#7A7873]">Tap to upload a photo</p>
                <p className="text-xs text-[#B5B0AA] mt-1">Receipt, food, signage — anything works</p>
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileSelect} />
            <Button
              onClick={handleAnalyze}
              disabled={!imageFile}
              className="w-full btn-primary"
              data-testid="analyze-photo-btn"
            >
              <ImageIcon weight="bold" className="w-5 h-5 mr-2" />
              Analyze Photo
            </Button>
          </div>
        )}

        {/* Analyzing Step */}
        {step === 'analyzing' && (
          <div className="flex flex-col items-center py-12">
            <SpinnerGap className="w-10 h-10 animate-spin text-[#A94438] mb-4" />
            <p className="text-sm text-[#7A7873]">AI is analyzing your photo...</p>
            <p className="text-xs text-[#B5B0AA] mt-1">Extracting restaurant, date, and bill details</p>
          </div>
        )}

        {/* Review Step */}
        {(step === 'review' || step === 'saving') && result && (
          <div className="space-y-4">
            {/* Photo preview */}
            {imagePreview && (
              <img src={imagePreview} alt="Visit" className="w-full h-32 object-cover rounded-xl" />
            )}

            {/* AI confidence */}
            <div className="flex items-center gap-2">
              <Badge className={result.confidence >= 0.7 ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                {result.photo_type || 'photo'}
              </Badge>
              <span className="text-xs text-[#9A9590]">
                {Math.round((result.confidence || 0.5) * 100)}% confident
              </span>
              {result.matched_restaurant && (
                <Badge className="bg-[#D1E2C4] text-[#4B5945]">
                  <Check weight="bold" className="w-3 h-3 mr-1" />
                  Matched: {result.matched_restaurant.name}
                </Badge>
              )}
            </div>

            {/* Editable fields */}
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-[#7A7873]">Restaurant Name</Label>
                <Input
                  value={restaurantName}
                  onChange={(e) => setRestaurantName(e.target.value)}
                  placeholder="Restaurant name"
                  data-testid="photo-restaurant-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#7A7873]">Date</Label>
                  <Input type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} data-testid="photo-visit-date" />
                </div>
                <div>
                  <Label className="text-xs text-[#7A7873]">Meal</Label>
                  <select
                    value={mealType}
                    onChange={(e) => setMealType(e.target.value)}
                    className="w-full text-sm border border-[#E8DCD0] rounded-lg px-3 py-2 bg-white"
                    data-testid="photo-meal-type"
                  >
                    <option value="">Select...</option>
                    <option value="breakfast">Breakfast</option>
                    <option value="brunch">Brunch</option>
                    <option value="lunch">Lunch</option>
                    <option value="dinner">Dinner</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-[#7A7873]">Bill (R)</Label>
                  <Input type="number" value={billTotal} onChange={(e) => setBillTotal(e.target.value)} placeholder="0" data-testid="photo-bill-total" />
                </div>
                <div>
                  <Label className="text-xs text-[#7A7873]">Tip (R)</Label>
                  <Input type="number" value={tip} onChange={(e) => setTip(e.target.value)} placeholder="0" data-testid="photo-tip" />
                </div>
                <div>
                  <Label className="text-xs text-[#7A7873]">Party</Label>
                  <Input type="number" value={partySize} onChange={(e) => setPartySize(e.target.value)} placeholder="2" data-testid="photo-party-size" />
                </div>
              </div>

              {/* Rating */}
              <div>
                <Label className="text-xs text-[#7A7873]">Rating</Label>
                <div className="flex gap-1 mt-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      onClick={() => setRating(s)}
                      className="p-1 transition-transform hover:scale-110"
                      data-testid={`photo-rating-${s}`}
                    >
                      <Star
                        weight={s <= rating ? 'fill' : 'regular'}
                        className={`w-6 h-6 ${s <= rating ? 'text-[#C4A35A]' : 'text-[#E8DCD0]'}`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Notable dishes */}
              {result.notable_dishes && result.notable_dishes.length > 0 && (
                <div>
                  <Label className="text-xs text-[#7A7873]">Dishes spotted</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {result.notable_dishes.map((d, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{d}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { resetState(); }} className="flex-1" data-testid="photo-cancel-btn">
                Start Over
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={step === 'saving' || !restaurantName.trim()}
                className="flex-1 btn-primary"
                data-testid="photo-confirm-btn"
              >
                {step === 'saving' ? (
                  <SpinnerGap className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Check weight="bold" className="w-5 h-5 mr-2" />
                )}
                Log Visit
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PhotoVisitModal;
